/**
 * Metrics tracking utility for QuickPoint Author
 * Sends events to the metrics API for analytics and marketing insights
 */

// decided by hostname, not build flags — the staging build previously read
// as non-PROD and beaconed uselessly at localhost:9001
// The graphs worker has no custom domain — it only lives at its workers.dev
// URL (like every other backend in clientConfig). graphs.quickpoint.me was
// never wired to DNS, so beacons there silently failed with ERR_NAME_NOT_RESOLVED.
const METRICS_ENDPOINT =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:9001/metrics/track'
    : 'https://graphs.quickpointme.workers.dev/metrics/track';

type EventType =
  | 'session_start'
  | 'story_create_opened'
  | 'story_created'
  | 'story_published'
  | 'story_opened'
  | 'story_deleted'
  | 'feature_used'
  | 'referral'
  | 'presentation_started'
  | 'share_clicked'
  | 'qr_generated'
  | 'image_uploaded'
  | 'filter_applied'
  | 'tour_started'
  | 'tour_completed'
  | 'login'
  | 'signup'
  // ad-funnel stages keyed by the marketing site's anonymous visitor id
  | 'author_visit'
  | 'signup_attributed';

type EventData = Record<string, string | number | boolean | undefined>;

// Generate a session ID that persists for the browser session
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('qp_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('qp_session_id', sessionId);
  }
  return sessionId;
};

// Check for UTM parameters on page load and track referral
const checkReferral = (userId: string): void => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  if (utmSource || utmMedium || utmCampaign) {
    const source = [utmSource, utmMedium, utmCampaign].filter(Boolean).join('/');
    track(userId, 'referral', { source, utm_source: utmSource ?? undefined, utm_medium: utmMedium ?? undefined, utm_campaign: utmCampaign ?? undefined });
  } else if (document.referrer && !document.referrer.includes('quickpoint.me')) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      track(userId, 'referral', { source: referrerHost });
    } catch {
      // Invalid referrer URL, ignore
    }
  }
};

/**
 * Track an event
 */
export const track = async (
  userId: string | undefined,
  eventType: EventType,
  eventData?: EventData
): Promise<void> => {
  if (!userId) return;

  try {
    const payload = {
      user_id: userId,
      event_type: eventType,
      event_data: eventData,
      session_id: getSessionId(),
    };

    // Use sendBeacon for better reliability (won't block page unload)
    if (navigator.sendBeacon) {
      navigator.sendBeacon(METRICS_ENDPOINT, JSON.stringify(payload));
    } else {
      // Fallback to fetch
      fetch(METRICS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // Silently fail - metrics shouldn't break the app
      });
    }
  } catch {
    // Silently fail - metrics shouldn't break the app
  }
};

/**
 * Track a feature being used (keyword, modifier, etc.)
 */
export const trackFeature = (userId: string | undefined, feature: string): void => {
  track(userId, 'feature_used', { feature });
};

/**
 * Initialize metrics tracking for a session
 * Call this when the user logs in or the app loads with a known user
 */
export const initMetrics = (userId: string): void => {
  track(userId, 'session_start');
  checkReferral(userId);
};

/**
 * Create a hook-friendly tracker that captures userId from Recoil
 */
export const createTracker = (userId: string | undefined) => ({
  track: (eventType: EventType, eventData?: EventData) => track(userId, eventType, eventData),
  trackFeature: (feature: string) => trackFeature(userId, feature),
  init: () => userId && initMetrics(userId),
});
